import React, { useEffect, useRef, useState } from 'react';
import './VerifyCodePanel.css';

// Shared six-box presentation. Submission remains the responsibility of its caller.
export default function VerificationCodeInput({ value, onChange, onComplete, onInput, disabled = false, inputsRef, id }) {
    const localRef = useRef([]);
    const refs = inputsRef || localRef;
    useEffect(() => { if (!inputsRef) localRef.current[0]?.focus(); }, [inputsRef]);
    const handleChange = (val, idx) => {
        if (disabled || !/^\d?$/.test(val)) return;
        const next = [...value];
        next[idx] = val;
        onChange(next);
        if (val && idx < 5) refs.current[idx + 1]?.focus();
        onInput?.();
        if (next.every(d => d !== '')) onComplete?.(next.join(''));
    };
    const handleKeyDown = (event, idx) => {
        if (disabled) { event.preventDefault(); return; }
        if (event.key === 'Backspace' && !value[idx] && idx > 0) refs.current[idx - 1]?.focus();
    };
    return <div className="code-inputs" role="group" aria-label="Six-digit verification code" onPaste={event => {
        if (disabled) { event.preventDefault(); return; }
        const paste = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!paste) return;
        const next = [...value];
        paste.split('').forEach((char, idx) => {
            next[idx] = char;
            if (refs.current[idx]) refs.current[idx].value = char;
        });
        onChange(next);
        refs.current[Math.min(paste.length, 5)]?.focus();
        if (paste.length === 6) onComplete?.(paste);
        event.preventDefault();
    }}>
        {value.map((digit, idx) => <input key={idx} id={id ? (idx === 0 ? id : `${id}-${idx + 1}`) : undefined}
            type="text" inputMode="numeric" maxLength="1" value={digit}
            aria-label={`Digit ${idx + 1}`} onChange={event => handleChange(event.target.value, idx)}
            onKeyDown={event => handleKeyDown(event, idx)} ref={element => (refs.current[idx] = element)}
            className="code-box" disabled={disabled} />)}
    </div>;
}

// Retain the existing login countdown markup/styles; admin receives its real API deadline.
export function VerificationCodeCountdown({ html, children, expiresAt }) {
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        if (!expiresAt) return;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [expiresAt]);
    if (html !== undefined) return <p className="code-expiry-timer" dangerouslySetInnerHTML={{ __html: html }} />;
    if (children !== undefined) return <p className="code-expiry-timer">{children}</p>;
    const deadline = new Date(expiresAt).getTime();
    if (!Number.isFinite(deadline)) return <p className="code-expiry-timer">Code expiration unavailable.</p>;
    const seconds = Math.max(0, Math.floor((deadline - now) / 1000));
    return <p className="code-expiry-timer">{seconds > 0 ? <>⏳ Code expires in <span className="emailMessageHilite">{String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}</span></> : '⏳ Code expired. Cancel and request a new code.'}</p>;
}
